<template>
  <div class="notification-overscreen">
    <div
      v-if="isShowingFBNotification && localFBNotification"
      @click="openNotificationSource()"
      class="notification-toast animate__animated animate__fadeIn"
    >
      <div class="icon-holder">
        <feather-icon icon="MessageSquareIcon" size="18" color="#1dc068" />
      </div>

      <div class="d-flex" style="flex-direction: column">
        <div
          style="font-size: 15px; font-weight: 500; color: #000000"
          v-dompurify-html="localFBNotification.data.title"
        />

        <div style="font-size: 16px; color: #6e6b7b" v-dompurify-html="localFBNotification.data.body" />
      </div>
    </div>

    <div
      v-if="isShowingWebsocketNotification && localWSNotification"
      @click="openWSNotificationSource()"
      class="notification-toast animate__animated animate__fadeIn"
    >
      <div class="icon-holder">
        <feather-icon icon="MessageSquareIcon" size="18" color="#1dc068" />
      </div>

      <div class="d-flex" style="flex-direction: column">
        <div
          style="font-size: 15px; font-weight: 500; color: #000000"
          v-dompurify-html="localWSNotification.data.notification.title"
        />

        <div
          style="font-size: 16px; color: #6e6b7b"
          v-dompurify-html="localWSNotification.data.notification.message"
        />
      </div>
    </div>

    <b-modal id="bv-opportunity-from-notification-toast" class="modal-dialog" hide-footer hide-header>
      <opportunity-tabs
        v-if="currentOpportunity"
        @close="$bvModal.hide('bv-opportunity-from-notification-toast')"
        :opportunity_id="currentOpportunity.id"
        :fromOmnichannelMobile="isFromOmnichannel"
      />
    </b-modal>

    <b-modal id="bv-task-from-notification-toast" class="modal-dialog" hide-footer hide-header>
      <task-form
        @saved="$bvModal.hide('bv-task-from-notification-toast')"
        @deleted="$bvModal.hide('bv-task-from-notification-toast')"
        @close="$bvModal.hide('bv-task-from-notification-toast')"
        :task-id="currentTaskId"
      />
    </b-modal>
  </div>
</template>

<script>
import OpportunityTabs from "@/views/forms/OpportunityTabs.vue";
import TaskForm from "@/views/forms/TaskForm.vue";
import notificationAudio from "@/assets/audio/omni-notification.mp3";

export default {
  components: {
    OpportunityTabs,
    TaskForm,
  },

  data() {
    return {
      localFBNotification: null,
      localWSNotification: null,

      taskToastInteracted: false,

      hasRedirectionRequest: false,

      currentOpportunity: null,
      isFromOmnichannel: false,

      currentTaskId: null,

      notificationRingTone: null,

      exportOrigins: [
        "ExportContactBatchJob",
        "ExportPdcaJob",
        "OpportunityImportJob",
        "OpportunityReportExportBatchJob",
      ],
    };
  },

  computed: {
    notification() {
      return this.$store.getters["firebase/getCurrentNotification"];
    },

    wsNotification() {
      return this.$store.state.omnichannelSocket.notification;
    },

    isShowingFBNotification() {
      return this.$store.getters["firebase/getIsShowingNotification"];
    },

    isShowingWebsocketNotification() {
      return this.$store.state.omnichannelSocket.showingNotification;
    },

    had_user_interaction_tick() {
      return this.$store.getters["externalPhone/getUserInteractionTick"];
    },

    isIos() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    },
  },

  watch: {
    notification: {
      handler() {
        this.handleNotification();
      },
      deep: true,
    },

    wsNotification: {
      handler() {
        this.handleWebsocketNotification();
      },
      deep: true,
    },

    had_user_interaction_tick() {
      this.prepareAudios();
    },
  },

  methods: {
    openTaskModal(taskId) {
      this.currentTaskId = parseInt(taskId);

      this.$bvModal.show("bv-task-from-notification-toast");
    },

    setStoreIsShowingNotification(bool) {
      this.$store.commit("firebase/setIsShowingNotification", bool);
    },

    playRingTone() {
      if (this.isIos || !this.notificationRingTone) return;

      this.notificationRingTone.currentTime = 0;

      try {
        const playPromise = this.notificationRingTone.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setTimeout(() => {
                this.notificationRingTone.pause();
              }, 1500);
            })
            .catch((error) => {
              console.log("Error playing notificationRingTone:", error);
            });
        }
      } catch (error) {
        console.log("Error in playRingTone:", error);
      }
    },

    handleNotification() {
      if (this.notification == null) return;

      if (Object.keys(this.notification).length !== 0) {
        this.localFBNotification = this.notification;

        this.handleShowNotification();

        this.handleDownloadExport();
      }
    },

    handleWebsocketNotification() {
      if (this.wsNotification == null) return;

      this.localWSNotification = this.wsNotification;

      if (this.localWSNotification.fromOmnichannel) {
        if (window.location.pathname.includes("/omnichannel")) {
          this.$store.commit("omnichannelSocket/setNotification", null);
          this.$store.commit("omnichannelSocket/setShowingNotification", false);

          return;
        }

        this.playRingTone();
      }

      this.$store.commit("omnichannelSocket/setShowingNotification", true);

      setTimeout(() => {
        this.$store.commit("omnichannelSocket/setShowingNotification", false);
      }, 5000);
    },

    handleShowNotification() {
      let data = this.localFBNotification.data;
      let isOmni = false;

      this.taskToastInteracted = false;

      const possibleOrigins = {
        Opportunity: () => {},

        Task: () => {},

        Dialog: () => {
          isOmni = true;
        },
      };

      if (possibleOrigins[data.origin]) possibleOrigins[data.origin]();

      if (isOmni && window.location.pathname.includes("/omnichannel")) {
        this.$store.commit("firebase/clearCurrentNotification");

        return;
      }

      if (isOmni) this.playRingTone();

      this.setStoreIsShowingNotification(true);

      setTimeout(() => {
        this.persistTaskCardIfUnread(data);

        this.$store.commit("firebase/clearCurrentNotification");

        this.setStoreIsShowingNotification(false);
      }, 5000);
    },

    persistTaskCardIfUnread(data) {
      if (data.origin !== "Task" || this.taskToastInteracted) return;

      this.$store.dispatch("taskNotificationCards/pushCard", {
        taskId: data.id,
        notificationId: data.notification_id,
        title: data.title,
        body: data.body,
        createdAt: new Date().toISOString(),
      });
    },

    handleDownloadExport() {
      const data = this.localFBNotification?.data;

      const isExport = this.exportOrigins.includes(data.origin);
      const exportReport = data.origin == "OpportunityReportExportBatchJob";

      if (isExport && data.id) {
        const fileName = data.filename;

        this.$store
          .dispatch(`exports/${exportReport ? "getDownloadLink" : "generateDownloadLink"}`, data.id)
          .then((response) => {
            let url;

            if (exportReport) {
              url = response.data.path;
            } else {
              url = window.URL.createObjectURL(new Blob([response]));
            }

            const link = document.createElement("a");

            link.href = url;

            link.setAttribute("download", fileName);

            document.body.appendChild(link);

            link.click();
          });
      }
    },

    openWSNotificationSource() {
      if (!this.localWSNotification) return;

      const data = this.localWSNotification.data.notification;

      const possibleOrigins = {
        Dialog: () => {
          this.isFromOmnichannel = true;

          this.requestOpportunityAndOpenModal(data.data.opportunity_id);
        },
      };

      if (possibleOrigins[data.origin]) {
        possibleOrigins[data.origin]();

        return;
      }

      this.$bvToast.toast("Não é possível ser direcionado para a origem dessa notificação.", {
        title: "Notificação não direcionável!",
        autoHideDelay: 2000,
        variant: "warning",
        toaster: "b-toaster-top-left",
        solid: true,
      });
    },

    openNotificationSource() {
      if (!this.localFBNotification) return;

      const data = this.localFBNotification.data;

      const possibleOrigins = {
        Dialog: () => {
          this.isFromOmnichannel = true;

          this.requestOpportunityAndOpenModal(data.opportunity_id);
        },

        Opportunity: () => {
          this.isFromOmnichannel = false;

          this.requestOpportunityAndOpenModal(data.id);
        },

        Task: () => {
          this.taskToastInteracted = true;

          this.$store.dispatch("taskNotificationCards/readAndRemove", {
            notificationId: data.notification_id,
          });

          this.openTaskModal(data.id);
        },

        LeadImportJob: () => {
          return;
        },
      };

      if (possibleOrigins[data.origin]) {
        possibleOrigins[data.origin]();

        return;
      }

      this.$bvToast.toast("Não é possível ser direcionado para a origem dessa notificação.", {
        title: "Notificação não direcionável!",
        autoHideDelay: 2000,
        variant: "warning",
        toaster: "b-toaster-top-left",
        solid: true,
      });
    },

    requestOpportunityAndOpenModal(opportunityId) {
      if (this.hasRedirectionRequest) return;

      this.hasRedirectionRequest = true;

      this.$store
        .dispatch("opportunityStore/fetchOne", opportunityId)
        .then((response) => {
          this.currentOpportunity = response.data;

          this.$bvModal.show("bv-opportunity-from-notification-toast");
        })
        .finally(() => {
          this.hasRedirectionRequest = false;
        });
    },

    async prepareAudios() {
      if (this.isIos) return;

      this.notificationRingTone = new Audio(notificationAudio);

      document.addEventListener(
        "touchstart",
        async () => {
          this.notificationRingTone.muted = true;

          this.notificationRingTone
            .play()
            .then(() => {
              this.notificationRingTone.pause();
              this.notificationRingTone.muted = false;
            })
            .catch((error) => console.log("Error playing notificationRingTone:", error));
        },
        { once: true },
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-overscreen {
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  pointer-events: none;

  .notification-toast {
    display: flex;
    align-items: center;
    position: fixed;
    top: 1rem;
    left: 50%;
    gap: 10px;
    transform: translateX(-50%);
    z-index: 9999999;
    padding: 1rem;
    border-radius: 6px;
    border: solid 1px #1dc068;
    border-left: solid 3px #1dc068;
    background: #e5f5ed;
    pointer-events: all;
    cursor: pointer;

    .icon-holder {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 34px;
      min-width: 34px;
      border-radius: 100%;
      background: #c4e3d3;
    }
  }
}
</style>
